import { useQuery } from "@apollo/client";
import { compact, uniq } from "lodash";
import {
  GetGoalOverviewRelatedEntitiesQueryQuery,
  GetGoalOverviewRelatedEntitiesQueryQueryVariables,
  GetGoalOverviewSelectedEntityQueryQuery,
  GoalOverviewRelatedEntityGoalFragmentFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Loading from "@components/loading/loading";
import {
  UserComboboxOption,
  UserComboboxOptionType,
} from "@components/user-combobox/user-combobox-list";
import { assertEdgesNonNull } from "@helpers/helpers";

import { getGoalOverviewRelatedEntitiesQuery } from "./graphql/get-goal-overview-related-entities-query";
import GoalOverviewRelatedEntitiesTable, {
  GoalOverviewUserOrTeamType,
} from "./related-entities-table";

const GoalOverviewRelatedEntity = ({
  title,
  loading,
  entities,
  entityLabel,
}: {
  title: string;
  loading?: boolean;
  entities: (GoalOverviewUserOrTeamType & {
    goals: GoalOverviewRelatedEntityGoalFragmentFragment[];
  })[];
  entityLabel: string;
}) => (
  <div>
    <div className="font-medium mb-2 flex items-center gap-2">
      {title}
      {loading === true && <Loading mini size={4} />}
    </div>
    <div className="flex flex-col gap-4">
      <GoalOverviewRelatedEntitiesTable
        entities={entities}
        entityLabel={entityLabel}
      />
    </div>
  </div>
);

type UserEntity = {
  __typename: "UserNode";
  id: number;
  name: string;
  avatar?: string | null;
};

type TeamEntity = {
  __typename: "TeamNode";
  id: number;
  title: string;
};

const GoalOverviewRelatedEntities = ({
  selected,
  selectedData,
  goalDueBetweenDates,
}: {
  selected: UserComboboxOption;
  selectedData: GetGoalOverviewSelectedEntityQueryQuery | undefined;
  goalDueBetweenDates: string[];
}) => {
  const label = useLabel();

  const directReports = selectedData?.user?.directReports
    ? assertEdgesNonNull(selectedData?.user?.directReports)
    : [];
  const directReportIds = directReports.map(({ id }) => id);
  const managers = selectedData?.user?.managers
    ? assertEdgesNonNull(selectedData?.user?.managers)
    : [];
  const managersIds = managers.map(({ id }) => id);
  const teamMembers = selectedData?.team?.members
    ? assertEdgesNonNull(selectedData?.team?.members)
    : [];
  const teamMemberIds = teamMembers.map(({ id }) => id);
  const ownerIds = uniq(
    compact([...directReportIds, ...managersIds, ...teamMemberIds]),
  );
  const orgTeams = selectedData?.organization?.teams
    ? assertEdgesNonNull(selectedData?.organization?.teams)
    : [];
  const orgTeamIds = orgTeams.map(({ id }) => id);
  const userTeams = selectedData?.user?.teams
    ? assertEdgesNonNull(selectedData?.user?.teams)
    : [];
  const userTeamIds = userTeams.map(({ id }) => id);
  const teamIds = uniq(compact([...userTeamIds, ...orgTeamIds]));

  // Query 1: Get entities (users/teams) without goals
  const { data: entitiesData, loading: entitiesLoading } = useQuery<
    GetGoalOverviewRelatedEntitiesQueryQuery,
    GetGoalOverviewRelatedEntitiesQueryQueryVariables
  >(getGoalOverviewRelatedEntitiesQuery, {
    variables: {
      goalOwners: ownerIds,
      goalTeams: teamIds,
      goalDueBetweenDates,
      hasOwners: ownerIds.length > 0,
      hasTeams: teamIds.length > 0,
    },
  });
  // return null;
  const ownerGoals = (
    entitiesData?.ownerGoals ? assertEdgesNonNull(entitiesData?.ownerGoals) : []
  ).map((node) => ({
    ...node,
    ownerIds:
      node.__typename === "GoalArtifactNode" && node.ownersList
        ? compact(node.ownersList.map((node2) => node2?.id))
        : [],
  }));
  const teamGoals = (
    entitiesData?.teamGoals ? assertEdgesNonNull(entitiesData?.teamGoals) : []
  ).map((node) => ({
    ...node,
    teamIds:
      node.__typename === "GoalArtifactNode" && node.teamsList
        ? compact(node.teamsList.map((node2) => node2?.id))
        : [],
  }));
  // Map entities to include their goals
  const mapUserToEntity = (user: UserEntity) =>
    ({
      ...user,
      goals: ownerGoals.filter(
        (goal) =>
          goal.__typename === "GoalArtifactNode" &&
          goal.ownerIds.includes(Number(user.id)),
      ),
    }) as GoalOverviewUserOrTeamType & {
      goals: GoalOverviewRelatedEntityGoalFragmentFragment[];
    };

  const mapTeamToEntity = (team: TeamEntity) =>
    ({
      ...team,
      goals: teamGoals.filter(
        (goal) =>
          goal.__typename === "GoalArtifactNode" &&
          goal.teamIds.includes(Number(team.id)),
      ),
    }) as GoalOverviewUserOrTeamType & {
      goals: GoalOverviewRelatedEntityGoalFragmentFragment[];
    };

  const loading = entitiesLoading;

  return (
    <>
      {directReports.length > 0 &&
        selected.type === UserComboboxOptionType.USER && (
          <GoalOverviewRelatedEntity
            title={`${selected.name}'s Reports`}
            entities={directReports.map(mapUserToEntity)}
            entityLabel="report"
            loading={loading}
          />
        )}

      {selected.type === UserComboboxOptionType.USER && (
        <GoalOverviewRelatedEntity
          title={`${selected.name}'s Manager`}
          entities={managers.map(mapUserToEntity)}
          entityLabel="manager"
          loading={loading}
        />
      )}

      {selected.type === UserComboboxOptionType.USER && (
        <GoalOverviewRelatedEntity
          title={`${selected.name}'s ${label("team", {
            pluralize: true,
            capitalize: true,
          })}`}
          entityLabel="team"
          entities={userTeams.map(mapTeamToEntity)}
          loading={loading}
        />
      )}

      {selected.type === UserComboboxOptionType.TEAM && (
        <GoalOverviewRelatedEntity
          title={`${selected.title}'s Members`}
          entityLabel="member"
          entities={teamMembers.map(mapUserToEntity)}
          loading={loading}
        />
      )}

      {selected.type === UserComboboxOptionType.ORG && (
        <GoalOverviewRelatedEntity
          title={`${selected.name} ${label("team", {
            pluralize: true,
            capitalize: true,
          })}`}
          entityLabel="team"
          entities={orgTeams.map(mapTeamToEntity)}
          loading={loading}
        />
      )}
    </>
  );
};

export default GoalOverviewRelatedEntities;
